/* @jsxImportSource @emotion/react */
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'

import { SMALL_BREAKPOINT } from '../../../../utils/breakpoints'
import type { DocLabel, DocNode } from './content'
import { INITIAL_TREE } from './content'
import { DocBody } from './DocBody'
import { firstDocId, flatten, getAncestorIds, walkReplace } from './helpers'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

// Compact KB on mobile: smaller base text + tighter horizontal padding on
// nested containers so more knowledge-base content fits in the same area.
// Targets descendants broadly because the KB stitches together multiple
// design-system primitives (SidebarShell, Breadcrumb, doc body) that each
// own their own paddings.
const compactKbCss = css`
  /* Hide scrollbars inside the KB while keeping content scrollable —
     the demo isn't meant to be interacted with so the visible scrollbar
     just adds noise. Applies on both desktop and mobile. */
  *::-webkit-scrollbar {
    display: none;
  }
  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  @media (max-width: ${SMALL_BREAKPOINT}px) {
    font-size: 12px;

    /* Squeeze horizontal padding on every utility-driven container so the
       text columns get more room — keeps vertical rhythm untouched. */
    .padding-horizontal-16,
    .padding-horizontal-12 {
      padding-left: 8px;
      padding-right: 8px;
    }
    .padding-left-16,
    .padding-left-12 {
      padding-left: 8px;
    }
    .padding-right-16,
    .padding-right-12 {
      padding-right: 8px;
    }
  }
`

export type DemoKnowledgeBaseHandle = {
  reset: () => void
  updateDoc: (id: string, content: string) => void
  navigate: (id: string) => void
  changeLabel: (id: string, label: DocLabel) => void
}

type DemoKnowledgeBaseProps = {
  // Overrides the default first-doc-with-content. Without this the demo
  // briefly paints the Company landing page before the loop navigates to
  // the first scenario's doc.
  initialDocId?: string
}

export const DemoKnowledgeBase = forwardRef<
  DemoKnowledgeBaseHandle,
  DemoKnowledgeBaseProps
>(({ initialDocId }, ref) => {
  const [tree, setTree] = useState<DocNode[]>(INITIAL_TREE)
  const [currentId, setCurrentId] = useState<string>(
    () => initialDocId ?? firstDocId(INITIAL_TREE)
  )
  const [docAnimationKey, setDocAnimationKey] = useState(0)

  const index = useMemo(() => flatten(tree), [tree])
  const current = index.get(currentId)
  const ancestorIds = useMemo(
    () => getAncestorIds(tree, currentId) ?? [],
    [tree, currentId]
  )
  const expanded = useMemo(() => new Set(ancestorIds), [ancestorIds])
  const breadcrumb = useMemo(
    () =>
      [...ancestorIds, currentId]
        .map((id) => index.get(id))
        .filter((node): node is DocNode => !!node),
    [ancestorIds, currentId, index]
  )

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        setTree(INITIAL_TREE)
        setCurrentId(initialDocId ?? firstDocId(INITIAL_TREE))
        setDocAnimationKey((key) => key + 1)
      },
      updateDoc: (id, content) =>
        setTree((prev) =>
          walkReplace(prev, id, (node) => ({ ...node, content }))
        ),
      navigate: (id) => {
        setCurrentId((prev) => {
          if (prev !== id) {
            setDocAnimationKey((key) => key + 1)
          }
          return id
        })
      },
      changeLabel: (id, label) =>
        setTree((prev) =>
          walkReplace(prev, id, (node) => ({ ...node, label }))
        ),
    }),
    [initialDocId]
  )

  return (
    <div
      className="position-relative height-full width-full"
      css={compactKbCss}
    >
      <div
        className="flexbox height-full width-full overflow-hidden border-small border-alpha-shade-45"
        style={{
          borderRadius: 16 + 4,
          background: '#f6f7f9',
          pointerEvents: 'none',
        }}
      >
        <Sidebar tree={tree} currentId={currentId} expanded={expanded} />
        <div className="flexbox-grow" style={{ minWidth: 0, padding: 4 }}>
          <main
            className={classNames(
              'height-full',
              'flexbox flexbox-column overflow-hidden',
              'background-shade-100 border-small border-shade-45'
            )}
            style={{ minWidth: 0, borderRadius: 16 }}
          >
            <Topbar breadcrumb={breadcrumb} />
            {current && (
              <DocBody current={current} animationKey={docAnimationKey} />
            )}
          </main>
        </div>
      </div>
    </div>
  )
})

DemoKnowledgeBase.displayName = 'DemoKnowledgeBase'
